/* eslint-disable no-undef-init */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  AuthenticationService,
  DefaultsService,
  ProfileService,
} from '../index';
import { MetricsService } from './metrics.service';

export type SortOptions = {
  sortOrder: 'desc' | 'asc';
  sortOn:
    | 'cat_class'
    | 'product_type_name'
    | 'revenue_total'
    | 'physical_utilization_current'
    | 'unit_count_current_available';
};

import {
  RouseCategory,
  ProductType,
  EquipmentAsset,
  AssetUnavailabilitySummary,
} from '../../models';
import { Subject } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { AssetAvailabilitySummary } from '../../models';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { FiltersService } from 'src/app/filters/filters.service';
import { SortOrder } from '../../models/sort-order.enum';

const DEBUG_CACHING = false;

const DISABLE_CACHING = true;

const APPLY_OLD_DEFAULT_SORTING = false;

export const PRODUCT_TYPES_CACHE: Map<string, any[]> = new Map();

export const getCacheKey = (body: any) => {
  const key = JSON.stringify(body);
  return key;
};

@Injectable()
export class EquipmentService extends MetricsService {
  assetUnavailabilitySummary?: AssetUnavailabilitySummary = undefined;
  assetUnavailabilitySummaryLoaded = new Subject<AssetUnavailabilitySummary>();
  assetAvailabilitySummary?: AssetAvailabilitySummary = undefined;
  assetAvailabilitySummaryLoaded = new Subject<AssetAvailabilitySummary>();
  equipmentAssets?: Array<EquipmentAsset> = undefined;
  equipmentAssetsLoaded = new Subject<Array<EquipmentAsset>>();
  rouseCategories?: Array<RouseCategory> = undefined;
  rouseCategoriesLoaded = new Subject<Array<RouseCategory>>();
  productTypes?: Array<ProductType> = undefined;
  productTypesLoaded = new Subject<Array<ProductType>>();
  productTypePageTitle: string = '';
  allAssetsId: number = -999;

  constructor(
    public http: HttpClient,
    public profileService: ProfileService,
    public translateService: TranslateService,
    public filtersService: FiltersService,
    protected defaultsService: DefaultsService,
    protected authenticationService: AuthenticationService
  ) {
    super(
      translateService,
      filtersService,
      defaultsService,
      authenticationService
    );
  }

  clearCache = () => {
    this.assetUnavailabilitySummary = undefined;
    this.assetAvailabilitySummary = undefined;
    this.equipmentAssets = undefined;
    this.rouseCategories = undefined;
    this.productTypes = undefined;
    PRODUCT_TYPES_CACHE.clear();
  };

  getCategories = () => {
    const url = `${environment.metricsApiUrl}/mobile-metrics/categories`;
    this.http
      .post<
        Array<RouseCategory>
      >(url, this.getMetricsPostBody(this.profileService.getSelectedCustomersIds(), this.profileService.getSelectedBranchIds(), this.profileService.getSelectedRegionIds(), this.profileService.getSelectedDistrictIds()))
      .subscribe(
        (categories) => {
          // TODO: Is this a bug? Shouldn't it be > 0?
          if (categories && categories.length > 1) {
            this.rouseCategories = [...categories];
          } else {
            categories = [];
          }
          this.rouseCategoriesLoaded.next(this.rouseCategories || []);
        },
        (error) => {
          if (error.status === '401') {
            Sentry.captureException(error);
          }
        }
      );
  };

  getProductTypes = (
    categoryId: number,
    page: number = 1,
    pageSize = 100,
    productTypeId?: number
  ) => {
    console.log(
      'equipment.service.getProductTypes  called',
      categoryId,
      page,
      productTypeId
    );
    const sorting: Partial<SortOptions> = this.getProductTypeSorting() || {};
    const url = `${environment.metricsApiUrl}/mobile-metrics/product-types`;
    let body = this.getMetricsPostBody(
      this.profileService.getSelectedCustomersIds(),
      this.profileService.getSelectedBranchIds(),
      this.profileService.getSelectedRegionIds(),
      this.profileService.getSelectedDistrictIds()
    );

    if (categoryId !== this.allAssetsId) {
      body.filters.and.push({ in: [{ var: 'category_id' }, [categoryId]] });
    }

    if (productTypeId) {
      body.filters.and.push({
        in: [{ var: 'product_type_id' }, [productTypeId]],
      });
    }

    body = { ...body, page, pageSize, ...sorting };

    // Reset product types whenever we load the first page.
    if (page === 1) {
      this.productTypes = undefined;
    }

    const cacheKey = getCacheKey(body);
    if (DEBUG_CACHING) {
      console.log(
        'equipment.service.getProductTypes  ',
        categoryId,
        page,
        sorting,
        'cache',
        cacheKey
      );
    }

    if (PRODUCT_TYPES_CACHE.has(cacheKey) && !DISABLE_CACHING) {
      if (DEBUG_CACHING) {
        console.log('   FOUND CACHE:', cacheKey);
      }

      const moreProductTypes = PRODUCT_TYPES_CACHE.get(
        cacheKey
      ) as ProductType[];
      this.updateProductTypes(moreProductTypes);
      this.productTypesLoaded.next(this.productTypes || []);
      return;
    }

    if (DEBUG_CACHING) {
      console.log('   NO CACHE', cacheKey);
    }

    console.log(
      'equipment.service.getProductTypes  should make request',
      url,
      body
    );
    this.http.post<Array<ProductType>>(url, body).subscribe(
      (moreProductTypes) => {
        if (DEBUG_CACHING) {
          console.log('   CACHING', cacheKey, moreProductTypes);
        }

        PRODUCT_TYPES_CACHE.set(cacheKey, moreProductTypes);
        if (moreProductTypes.length > 0) {
          this.updateProductTypes(moreProductTypes);

          // TODO: Do we still need this default sorting?
          if (!this.getProductTypeSorting() && APPLY_OLD_DEFAULT_SORTING) {
            this.sortProductTypes(this.productTypes as []);
          }
        } else {
          if (DEBUG_CACHING) {
            console.log('equipment.service list is complete');
          }
        }

        this.productTypesLoaded.next(this.productTypes || []);
      },
      (error) => {
        if (error.status === '401') {
          Sentry.captureException(error);
        }
      }
    );
  };

  protected updateProductTypes(moreProductTypes: ProductType[]): void {
    if (!this.productTypes) {
      this.productTypes = [...moreProductTypes];
    } else {
      this.productTypes?.push(...moreProductTypes);
    }
  }

  getProductTypeSorting(): SortOptions | null {
    const sortType = this.profileService.getSelectedProductSortType();
    if (!sortType) {
      return null;
    }
    const sortOrder = sortType.direction === SortOrder.DESC ? 'desc' : 'asc';
    const sortOn = sortType.field;
    const result = { sortOrder, sortOn } as SortOptions;
    return result;
  }

  sortProductTypes = (productTypes: Array<ProductType>) => {
    if (
      productTypes &&
      productTypes instanceof Array &&
      productTypes.length > 0
    ) {
      if (
        !this.profileService.selectedCustomers ||
        this.profileService.selectedCustomers?.length === 0
      ) {
        productTypes?.sort(
          (a, b) =>
            Number(b.revenue_total) - Number(a.revenue_total) ||
            a.product_type_name.localeCompare(b.product_type_name)
        );
      } else {
        productTypes?.sort(
          (a, b) =>
            Number(b.customer_revenue) - Number(a.customer_revenue) ||
            Number(b.revenue_total) - Number(a.revenue_total) ||
            a.product_type_name.localeCompare(b.product_type_name)
        );
      }
      //}
    }
  };

  getAssetAvailabilitySummary = (categoryId: number, productTypeId: number) => {
    const url = `${environment.metricsApiUrl}/mobile-metrics/asset-availability/available`;

    const body = this.getMetricsPostBody(
      this.profileService.getSelectedCustomersIds(),
      this.profileService.getSelectedBranchIds(),
      this.profileService.getSelectedRegionIds(),
      this.profileService.getSelectedDistrictIds()
    );
    if (categoryId !== this.allAssetsId) {
      body.filters.and.push({ in: [{ var: 'category_id' }, [categoryId]] });
    }
    if (productTypeId !== this.allAssetsId) {
      body.filters.and.push({
        in: [{ var: 'product_type_id' }, [productTypeId]],
      });
    }
    this.http.post<AssetAvailabilitySummary>(url, body).subscribe(
      (avs) => {
        this.assetAvailabilitySummary = avs;
        this.assetAvailabilitySummaryLoaded.next(this.assetAvailabilitySummary);
      },
      (error) => {
        if (error.status === '401') {
          Sentry.captureException(error);
        }
      }
    );
  };

  getAssetUnavailabilitySummary = (
    categoryId: number,
    productTypeId: number
  ) => {
    const url = `${environment.metricsApiUrl}/mobile-metrics/asset-availability/unavailable`;
    const body = this.getMetricsPostBody(
      this.profileService.getSelectedCustomersIds(),
      this.profileService.getSelectedBranchIds(),
      this.profileService.getSelectedRegionIds(),
      this.profileService.getSelectedDistrictIds()
    );
    if (categoryId !== this.allAssetsId) {
      body.filters.and.push({ in: [{ var: 'category_id' }, [categoryId]] });
    }
    if (productTypeId !== this.allAssetsId) {
      body.filters.and.push({
        in: [{ var: 'product_type_id' }, [productTypeId]],
      });
    }
    this.http.post<AssetUnavailabilitySummary>(url, body).subscribe(
      (avs) => {
        this.assetUnavailabilitySummary = avs;
        this.assetUnavailabilitySummaryLoaded.next(
          this.assetUnavailabilitySummary
        );
      },
      (error) => {
        if (error.status === '401') {
          Sentry.captureException(error);
        }
      }
    );
  };

  getProductTypeAssets = (
    categoryId: number,
    productTypeId: number,
    equipmentStatus: string | undefined,
    unavailableStatus: string | null,
    page: number,
    pageSize: number = 10,
    queryParams?: any
  ) => {
    const status = equipmentStatus?.toLowerCase();
    const url = `${environment.metricsApiUrl}/mobile-metrics/assets`;

    // When the user clicks on the geography level links we need to strip out all of the
    // other geography filters that may be selected. The getSelected*Ids methods below are older code.
    // We are passing them an array if the geography level is defined, in that way we are stripping out
    // the other geography filter lists. The company list is always empty, but we have to strip out the other filter
    // list in this case as well.

    const validGeoAvailabilityMetrics = [
      'branch',
      'region',
      'district',
      'company',
    ];

    let branchIds: number[] | undefined = undefined;
    let regionIds: number[] | undefined = undefined;
    let districtIds: number[] | undefined = undefined;

    if (
      queryParams?.geography &&
      validGeoAvailabilityMetrics.includes(queryParams?.geography)
    ) {
      // When clicking a geography stat, use the specific IDs for that level
      // Allow parent geographic filters from localStorage (e.g., region when district is clicked)
      // but clear child level filters
      if (queryParams.geography === 'branch') {
        branchIds = queryParams.ids || [];
        // Clear nothing - branch is the lowest level
      } else if (queryParams.geography === 'district') {
        districtIds = queryParams.ids || [];
        branchIds = []; // Clear child level (branch)
      } else if (queryParams.geography === 'region') {
        regionIds = queryParams.ids || [];
        districtIds = []; // Clear child levels (district and branch)
        branchIds = [];
      } else {
        // Company level - clear all geographic filters to show all assets
        regionIds = [];
        districtIds = [];
        branchIds = [];
      }
    }

    const body = this.getMetricsPostBody(
      this.profileService.getSelectedCustomersIds(),
      this.profileService.getSelectedBranchIds(branchIds),
      this.profileService.getSelectedRegionIds(regionIds),
      this.profileService.getSelectedDistrictIds(districtIds)
    );

    body.page = page;
    body.pageSize = pageSize;

    if (categoryId !== this.allAssetsId) {
      body.filters.and.push({ in: [{ var: 'category_id' }, [categoryId]] });
    }

    if (productTypeId !== this.allAssetsId) {
      body.filters.and.push({
        in: [{ var: 'product_type_id' }, [productTypeId]],
      });
    }

    // When the user clicks on the graph on the asset-unavailability page
    // we show all unavailable items. In order to get them
    // we need to set the is_available and the on_rent filters to false,
    // with no equipment_status. Sub-pages of that section are filtered
    // normally.
    if (status !== 'unavailable') {
      // body.filters.and.push({
      //   in: [{ var: 'equipment_status' }, [equipmentStatus]],
      // });
      if (status === 'available') {
        body.filters.and.push({
          in: [{ var: 'is_available' }, [true]],
        });
        body.filters.and.push({
          in: [{ var: 'on_rent' }, [false]],
        });
      }

      if (status === 'onrent') {
        body.filters.and.push({
          in: [{ var: 'is_available' }, [false]],
        });
        body.filters.and.push({
          in: [{ var: 'on_rent' }, [true]],
        });
      }
    } else {
      body.filters.and.push({
        in: [{ var: 'is_available' }, [false]],
      });
      body.filters.and.push({
        in: [{ var: 'on_rent' }, [false]],
      });

      if (unavailableStatus) {
        body.filters.and.push({
          in: [{ var: 'equipment_status' }, [unavailableStatus]],
        });
      }
    }
    //body.OnRent = OnRent.Both;
    this.http.post<Array<EquipmentAsset>>(url, body).subscribe(
      (equipmentAssets) => {
        this.equipmentAssets = [...equipmentAssets];
        this.equipmentAssetsLoaded.next(this.equipmentAssets);
      },
      (error) => {
        if (error.status === '401') {
          Sentry.captureException(error);
        }
      }
    );
  };
}
